import { Controller } from 'stimulus';

/**
 * Progressive email/password login form inside the contest vote modal.
 *
 * Flow:
 * - First "Continue" submit: calls /contests/check_email to determine login method.
 *   - "password"              → reveals the password field; second submit goes to Devise.
 *   - "instant_access_link"  → POSTs to /contests/instant_access_links, then swaps the form
 *                               for the "Check your inbox" state.
 *   - "not_found"            → shows an inline error below the email field.
 *
 * @example
 *   <div data-controller="contests--email-login"
 *        data-contests--email-login-check-email-url-value="/contests/check_email"
 *        data-contests--email-login-send-login-link-url-value="/contests/instant_access_links"
 *        data-contests--email-login-origin-value="/contests/slug/submissions/slug?vote=1">
 *     <form data-action="submit->contests--email-login#continue"
 *           data-contests--email-login-target="emailSection">
 *       <input data-contests--email-login-target="email" type="email" required />
 *       <p data-contests--email-login-target="notFoundError" hidden> ... </p>
 *       <div data-contests--email-login-target="passwordSection" hidden> ... </div>
 *     </form>
 *     <div data-contests--email-login-target="inboxState" hidden>
 *       <strong data-contests--email-login-target="inboxEmail"></strong>
 *       <button data-contests--email-login-target="resendButton"
 *               data-action="click->contests--email-login#resend">Re-send</button>
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = [
    'email',
    'emailSection',
    'passwordSection',
    'password',
    'inboxState',
    'inboxEmail',
    'resendButton',
    'notFoundError',
  ];
  static values = { checkEmailUrl: String, sendLoginLinkUrl: String, origin: String };

  static RESEND_COOLDOWN_MS = 30000;

  async continue(event) {
    if (this.#isPasswordHidden()) {
      event.preventDefault();
      const loginMethod = await this.#checkEmail();
      if (loginMethod === 'password') {
        this.#clearNotFoundError();
        this.#showPasswordField();
      } else if (loginMethod === 'instant_access_link') {
        this.#clearNotFoundError();
        await this.#sendLoginLink();
        this.#showInboxState();
      } else if (loginMethod === 'not_found') {
        this.#showNotFoundError();
      }
    }
  }

  async resend() {
    const button = this.resendButtonTarget;
    const originalHTML = button.innerHTML;

    button.disabled = true;
    await this.#sendLoginLink();
    button.innerHTML = 'Sent!';

    setTimeout(() => {
      button.disabled = false;
      button.innerHTML = originalHTML;
    }, this.constructor.RESEND_COOLDOWN_MS);
  }

  #isPasswordHidden() {
    return this.passwordSectionTarget.hidden;
  }

  #showPasswordField() {
    this.passwordSectionTarget.hidden = false;
    this.passwordTarget.required = true;
    this.passwordTarget.focus();
  }

  #showInboxState() {
    this.emailSectionTarget.hidden = true;
    this.inboxEmailTarget.textContent = this.emailTarget.value;
    this.inboxStateTarget.hidden = false;
  }

  #showNotFoundError() {
    this.emailTarget.classList.add('field--error');
    this.notFoundErrorTarget.hidden = false;
    this.emailTarget.addEventListener('input', () => this.#clearNotFoundError(), { once: true });
  }

  #clearNotFoundError() {
    this.emailTarget.classList.remove('field--error');
    this.notFoundErrorTarget.hidden = true;
  }

  async #checkEmail() {
    const url = new URL(this.checkEmailUrlValue, window.location.origin);
    url.searchParams.set('email', this.emailTarget.value);
    const response = await fetch(url);
    const data = await response.json();
    return data.type;
  }

  async #sendLoginLink() {
    const formData = new FormData();
    formData.append('email', this.emailTarget.value);
    formData.append('origin', this.originValue);
    formData.append(
      'authenticity_token',
      document.querySelector('meta[name="csrf-token"]')?.content
    );
    await fetch(this.sendLoginLinkUrlValue, { method: 'POST', body: formData });
  }
}
